import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import SeverityBadge from '@/components/apex/SeverityBadge';
import { FrameworkBadge } from '@/components/apex/Badges';
import GovernanceTruthCard from '@/components/apex/GovernanceTruthCard';
import EvidenceChain from '@/components/apex/EvidenceChain';
import InvestigationTimeline from '@/components/apex/InvestigationTimeline';
import ActionMenu from '@/components/apex/ActionMenu';
import { Button } from '@/components/apex/../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Download, Clock, Users, Shield, ArrowRight, ScrollText, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { SkeletonCard } from '@/components/apex/Skeleton';
import EvidenceLineage from '@/components/apex/EvidenceLineage';
import GovernanceGraphFull from '@/components/apex/GovernanceGraphFull';
import InvestigationReplay from '@/components/apex/InvestigationReplay';
import RecordDrawer from '@/components/apex/RecordDrawer';
import useListDrawer from '@/hooks/useListDrawer';

export default function Investigations() {
  const { seed, loading, API } = useApp();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedId = params.get('id') || 'INV-2026-042';

  const inv = useMemo(() => seed?.investigations.find((i) => i.id === selectedId), [seed, selectedId]);

  // If URL contains ?replay=N or ?autoplay=1, land directly on the Replay tab.
  const initialTab = (params.get('replay') !== null || params.get('autoplay') === '1') ? 'replay' : 'overview';

  const downloadPacket = (id) => {
    const url = `${API}/evidence-packets/${id}/pdf`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `apex-packet-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const remList = inv?.remediation ?? [];
  const {
    active: activeRem,
    setActive: setActiveRem,
    openPrev: remPrev,
    openNext: remNext,
    index: remIndex,
    total: remTotal,
  } = useListDrawer({ items: remList, paramKey: 'rem' });

  const buildRemRecord = (r) => {
    const debtK = r.debt_reduction_usd / 1000;
    const isCritical = r.id?.includes('042-1');
    return {
      idTag: r.id,
      title: r.title,
      severity: isCritical ? 'critical' : 'high',
      subtitle: 'Remediation step in this investigation · estimated effort and debt reduction below',
      verdict: {
        tone: 'improving',
        label: `${r.effort_minutes}-minute fix`,
        sub: `Closing this step removes one contributor to the investigation's exposure window. Combined with the other ${(inv?.remediation?.length ?? 1) - 1} step(s), the investigation becomes auto-resolvable.`,
      },
      signal: {
        tone: 'improving',
        lead: 'Outcome on close:',
        body: 'APEX auto-marks this step complete the moment the next CloudTrail evidence cycle confirms the change. The investigation\'s evidence packet inherits the closure timestamp without any manual upload.',
      },
      meta: [
        { label: 'Effort budget', value: `${r.effort_minutes}m`, mono: true, sub: 'Smallest reversible change' },
        { label: 'Debt reduction', value: `−$${debtK.toFixed(0)}K`, mono: true, tone: 'success', sub: 'Calculated from exposure × sensitivity', delta: 'down' },
        { label: 'Due', value: r.due, mono: true, sub: 'Per remediation calendar' },
        { label: 'Impact area', value: r.impact, sub: 'Resources touched on close' },
      ],
      sections: [
        {
          title: 'Steps to investigation closure',
          type: 'chart',
          chart: {
            kind: 'progress',
            steps: [
              { label: 'Scope confirmed', sub: 'Investigation review', state: 'done' },
              { label: 'Change applied', sub: 'PR merged', state: isCritical ? 'active' : 'done' },
              { label: 'Evidence verified', sub: 'CloudTrail cycle', state: isCritical ? 'pending' : 'active' },
              { label: 'Packet refreshed', sub: 'Audit pull', state: 'pending' },
            ],
          },
        },
        {
          title: 'Operational signals',
          type: 'kpis',
          kpis: [
            { label: 'Time-to-close', value: `${r.effort_minutes}m`, sub: 'Engineering effort budgeted', tone: 'lens', delta: 'flat' },
            { label: 'Audit weight', value: isCritical ? 'Material' : 'Routine', sub: isCritical ? 'Surfaces in next audit pull' : 'Standard quarterly evidence', tone: isCritical ? 'warning' : 'success', delta: isCritical ? 'up' : 'flat' },
            { label: 'Reversibility', value: 'High', sub: 'Pipeline-driven with rollback path', tone: 'success', delta: 'flat' },
          ],
        },
        {
          title: 'Steps',
          type: 'list',
          items: [
            'Confirm the scope is unchanged from when this investigation was opened.',
            'Apply the change via the documented pipeline (PR + reviewer) — no console actions.',
            'APEX will auto-mark this action complete when verification arrives in CloudTrail.',
            'Re-export the evidence packet so closure is reflected in the next audit pull.',
          ],
        },
      ],
      actions: [
        { label: 'Open Remediation Center', onClick: () => navigate('/lens/remediation'), primary: true },
      ],
    };
  };

  if (loading || !seed) return <SkeletonCard />;
  if (!inv) return <div>Investigation not found</div>;

  const select = (id) => setParams({ id });

  return (
    <div className="space-y-5" data-testid={`investigation-${inv.id}`}>
      {/* Header */}
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Investigations · {seed.investigations.length} active</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Connected investigations</h1>
        <p className="text-sm text-apex-neutral mt-1">One investigation, one connected story. Every claim traces to immutable evidence.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-5">
        {/* Investigation list */}
        <div className="lg:sticky lg:top-20 self-start">
          {/* Mobile selector */}
          <div className="lg:hidden mb-3">
            <Select value={selectedId} onValueChange={select}>
              <SelectTrigger data-testid="investigation-select-mobile">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {seed.investigations.map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.id} · {i.title.slice(0, 36)}…</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Desktop list */}
          <div className="hidden lg:flex flex-col gap-2" data-testid="investigation-list">
            {seed.investigations.map((i) => {
              const active = i.id === selectedId;
              return (
                <button
                  key={i.id}
                  onClick={() => select(i.id)}
                  data-testid={`inv-list-${i.id}`}
                  className={`text-left px-3 py-3 rounded-md border transition-all ${active ? 'bg-white border-l-4 border-l-apex-lens border-slate-200 shadow-sm' : 'bg-white/60 border-slate-200 hover:bg-white'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-mono text-apex-neutral">{i.id}</span>
                    <SeverityBadge severity={i.severity} size="sm" icon={false} />
                  </div>
                  <p className="text-xs font-semibold text-apex-ink mt-1.5 leading-tight line-clamp-2">{i.title}</p>
                  <p className="text-[10px] text-apex-neutral mt-1">{i.days_open}d · {i.team}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <motion.div
          key={inv.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-5"
        >
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <span className="text-[10px] font-mono text-apex-neutral">{inv.id}</span>
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-apex-ink mt-1">{inv.title}</h2>
              </div>
              <ActionMenu context={inv.id} />
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <SeverityBadge severity={inv.severity} />
              {inv.scope?.map((s) => <FrameworkBadge key={s} framework={s} />)}
              <span className="inline-flex items-center gap-1 text-[11px] text-apex-neutral border border-slate-200 rounded px-2 py-0.5"><Clock className="w-3 h-3" />{inv.days_open} days open</span>
              <span className="inline-flex items-center gap-1 text-[11px] text-apex-neutral border border-slate-200 rounded px-2 py-0.5"><Users className="w-3 h-3" />{inv.team}</span>
              <span className="inline-flex items-center gap-1 text-[11px] text-apex-neutral border border-slate-200 rounded px-2 py-0.5"><Shield className="w-3 h-3" />{inv.framework_controls?.length} controls</span>
            </div>
            <p className="text-sm text-apex-ink-2 leading-relaxed">{inv.summary}</p>
          </div>

          {inv.timeline?.length > 0 ? (
            <Tabs defaultValue={initialTab} className="w-full">
              <TabsList data-testid="investigation-tabs">
                <TabsTrigger value="overview" data-testid="tab-overview">Investigation</TabsTrigger>
                <TabsTrigger value="lineage" data-testid="tab-lineage">Evidence lineage</TabsTrigger>
                <TabsTrigger value="graph" data-testid="tab-graph">Governance Graph</TabsTrigger>
                <TabsTrigger value="replay" data-testid="tab-replay">Replay</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-4">
                <div className="space-y-6">
                  {/* Cinematic incident replay — promoted */}
                  <div data-testid="overview-replay-block">
                    <div className="flex items-center gap-2 mb-3">
                      <Play className="w-3.5 h-3.5 text-apex-lens" />
                      <h3 className="text-sm font-semibold text-apex-ink">Incident replay</h3>
                      <span className="text-[10px] font-mono text-apex-neutral">· press play or click any phase to advance</span>
                    </div>
                    <InvestigationReplay inv={inv} />
                  </div>

                  {/* Existing 3-column detail block — kept as supporting context */}
                  <div data-testid="overview-detail-block">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-3.5 h-3.5 text-apex-neutral" />
                      <h3 className="text-sm font-semibold text-apex-ink">Detailed event log · affected assets · evidence summary</h3>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              {/* Timeline */}
              <div className="xl:col-span-1 bg-white rounded-lg border border-slate-200 p-5">
                <h3 className="text-sm font-bold text-apex-ink mb-4">Investigation Timeline</h3>
                <InvestigationTimeline events={inv.timeline} />
              </div>

              {/* Affected assets — Governance Truth Cards */}
              <div className="xl:col-span-1">
                <h3 className="text-sm font-bold text-apex-ink mb-3">Governance truths</h3>
                <p className="text-[11px] text-apex-neutral mb-3 leading-relaxed">
                  Each card states one verifiable claim about an affected asset, anchored to a proof status and evidence chain.
                </p>
                <div className="grid grid-cols-1 gap-3" data-testid="governance-truths">
                  {inv.assets.map((a, i) => (
                    <GovernanceTruthCard
                      key={a.name}
                      index={i}
                      testId={`truth-${a.name}`}
                      truth={buildTruth(a, i, inv, navigate)}
                    />
                  ))}
                </div>
              </div>

              {/* Evidence summary */}
              <div className="xl:col-span-1 space-y-3">
                <h3 className="text-sm font-bold text-apex-ink">Evidence Summary</h3>
                <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-2 font-mono text-xs">
                  <Row label="CloudTrail events analyzed" value={inv.evidence.events_analyzed.toLocaleString()} />
                  <Row label="Evidence records created" value={inv.evidence.evidence_records} />
                  <Row label="Framework controls affected" value={inv.evidence.controls_affected} />
                  {inv.framework_controls?.map((c) => (
                    <div key={c} className="text-[11px] text-apex-neutral pl-3">— {c}</div>
                  ))}
                  <Row label="Evidence packet" value={inv.evidence.packet_status} valueClass="text-apex-success" />
                  <div className="pt-2 border-t border-slate-200 truncate text-[10px] text-apex-lens">Hash: {inv.evidence.packet_hash?.slice(0, 28)}…</div>
                </div>
                <Button
                  data-testid="download-packet"
                  onClick={() => downloadPacket(inv.id)}
                  className="w-full bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5"
                >
                  <Download className="w-4 h-4" />
                  Download Signed Packet
                </Button>
                <EvidenceChain
                  events={inv.evidence.events_analyzed}
                  evidenceRecords={inv.evidence.evidence_records}
                  evidenceId={inv.evidence.evidence_id}
                  hash={inv.evidence.packet_hash?.slice(0, 24) + '…'}
                />
              </div>
            </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="lineage" className="mt-4">
                <EvidenceLineage inv={inv} />
              </TabsContent>
              <TabsContent value="graph" className="mt-4">
                <div className="rounded-xl overflow-hidden bg-apex-shell p-4 sm:p-5 min-h-[640px] flex">
                  <GovernanceGraphFull />
                </div>
              </TabsContent>
              <TabsContent value="replay" className="mt-4">
                <InvestigationReplay inv={inv} />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="bg-white rounded-lg border border-dashed border-slate-300 p-8 text-center">
              <ScrollText className="w-8 h-8 text-slate-300 mx-auto mb-2" strokeWidth={1.4} />
              <p className="text-sm font-semibold">Investigation in progress</p>
              <p className="text-xs text-apex-neutral mt-1 max-w-md mx-auto">Detailed timeline, asset map, and evidence packet will assemble as APEX correlates more events.</p>
            </div>
          )}

          {/* Remediation plan */}
          {inv.remediation?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-apex-ink mb-3">Remediation Plan</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {inv.remediation.map((r, i) => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                    data-testid={`rem-${r.id}`}
                    onClick={() => setActiveRem(r)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveRem(r); } }}
                    className="bg-white rounded-lg border border-slate-200 p-4 cursor-pointer hover:shadow-sm hover:-translate-y-0.5 transition-all"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-semibold text-apex-ink leading-snug flex-1">{i + 1}. {r.title}</h4>
                      <span className="text-[10px] font-mono text-apex-neutral shrink-0">{r.id}</span>
                    </div>
                    <p className="text-xs text-apex-neutral mt-1.5">{r.impact}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-apex-neutral font-mono">
                      <span>⏱ {r.effort_minutes} min</span>
                      <span>·</span>
                      <span className="text-apex-success">−${(r.debt_reduction_usd / 1000).toFixed(0)}K debt</span>
                      <span>·</span>
                      <span>Due {r.due}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                      <button onClick={(e) => { e.stopPropagation(); setActiveRem(r); }} className="text-xs text-apex-lens font-semibold inline-flex items-center gap-1">
                        Open detail
                        <ArrowRight className="w-3 h-3" />
                      </button>
                      <ActionMenu context={r.id} testId={`rem-actions-${r.id}`} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <RecordDrawer
        open={!!activeRem}
        onOpenChange={(o) => !o && setActiveRem(null)}
        record={activeRem ? buildRemRecord(activeRem) : null}
        testId="inv-rem-drawer"
        onPrev={remPrev}
        onNext={remNext}
        position={remIndex}
        total={remTotal}
      />
    </div>
  );
}

function Row({ label, value, valueClass = '' }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-apex-neutral">{label}</span>
      <span className={`font-semibold text-apex-ink ${valueClass}`}>{value}</span>
    </div>
  );
}

// Build a governance "truth" — one verifiable claim per affected asset.
// For INV-2026-042 we hand-tune per asset (because the deck is narrative); other investigations
// fall back to a sensible synthesized truth based on asset metadata.
function buildTruth(asset, index, inv, navigate) {
  const TRUTHS_042 = {
    'stratum-payments-api': {
      severity: 'critical',
      claim: 'Production payments service is running a dev-origin container image deployed without the production pipeline. The image is 184 days old and carries 847 known CVEs.',
      verdict: { tone: 'breach', label: 'PCI breach' },
      proofStatus: 'PROVEN',
      depth: 9.2,
      sources: ['CloudTrail', 'ECS', 'ECR'],
      frameworks: ['PCI DSS 6.1', 'SOC 2 CC8.1'],
      evidenceId: 'EVD-INV042-payments-api',
      lastVerified: 'Apr 21, 2026',
      pivot: { label: 'Open container governance', onClick: () => navigate('/lens/containers') },
    },
    'stratum/payments-api:latest': {
      severity: 'high',
      claim: 'Image was pushed to the shared ECR repository by a developer identity, not the production CI pipeline. Lineage points back to stratum-dev-sandbox.',
      verdict: { tone: 'breach', label: 'Pipeline bypass' },
      proofStatus: 'PROVEN',
      depth: 9.0,
      sources: ['CloudTrail', 'ECR'],
      frameworks: ['SOC 2 CC8.1', 'ISO 27001 A.12.1.2'],
      evidenceId: 'EVD-INV042-ecr-push',
      lastVerified: 'Nov 28, 2025',
      pivot: { label: 'Open container lineage', onClick: () => navigate('/lens/containers') },
    },
    'lambda-payments-processor': {
      severity: 'high',
      claim: 'IAM role grants 847 actions but only 12 are exercised — 1.4% effective permissions. Overpermissioning enabled the cross-environment trust traversal.',
      verdict: { tone: 'risk', label: 'Overpermissioned' },
      proofStatus: 'PROVEN',
      depth: 9.4,
      sources: ['CloudTrail', 'IAM Access Analyzer'],
      frameworks: ['SOC 2 CC6.3', 'ISO 27001 A.9.4'],
      evidenceId: 'EVD-INV042-iam-role',
      lastVerified: 'Apr 14, 2026',
      pivot: { label: 'Open identity proof', onClick: () => navigate('/lens/identity') },
    },
    'stratum-pci-customer-data': {
      severity: 'critical',
      claim: 'PCI-classified S3 bucket received 3 GetObject calls during the trust traversal at 02:18 UTC, accessed by a role assumed from the dev account.',
      verdict: { tone: 'breach', label: 'Data accessed' },
      proofStatus: 'PROVEN',
      depth: 9.6,
      sources: ['CloudTrail', 'Macie', 'S3 Access Logs'],
      frameworks: ['PCI DSS 3.4', 'PCI DSS 10.1'],
      evidenceId: 'EVD-INV042-s3-access',
      lastVerified: 'Apr 14, 2026',
      pivot: { label: 'Open account landscape', onClick: () => navigate('/lens/accounts/stratum-payments-prod') },
    },
  };

  if (inv?.id === 'INV-2026-042' && TRUTHS_042[asset.name]) {
    return { asset: asset.name, kind: asset.type, ...TRUTHS_042[asset.name] };
  }

  // Generic fallback — derive from asset metadata for any other investigation.
  return {
    asset: asset.name,
    kind: asset.type,
    severity: index === 0 ? 'critical' : index < 3 ? 'high' : 'medium',
    claim: asset.detail ?? `${asset.type} flagged in this investigation. Provenance traced to seeded evidence sources.`,
    verdict: { tone: index === 0 ? 'breach' : 'risk', label: asset.label ?? 'Affected' },
    proofStatus: 'PROVEN',
    depth: 8.5 - index * 0.3,
    sources: ['CloudTrail', 'Config'],
    frameworks: inv?.framework_controls?.slice(0, 2) ?? [],
    evidenceId: `EVD-${inv?.id}-${asset.name}`,
    lastVerified: inv?.timeline?.[inv.timeline.length - 1]?.date ?? '—',
  };
}
